import { Box, Button, Grid, Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCommunityOptIn } from '../hook/useCommunityOptIn';
import { CommunityOptInStateType } from '../hook/useCommunityOptInReducer';
import { acceptCommunityOptIn } from '../hook/useCommunityOptInAction';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TERMS_CONDITIONS_URL,
  useUserInfoContext,
} from '@xolv-mybrightlink/common';
import {
  MessageBar,
  LoadingIndicator,
  ImageWithLoader,
} from '@xolv-mybrightlink/common';
import { useCallback } from 'react';

interface CommunityOptInShellProps {
  children: React.ReactNode;
}

export const CommunityOptInShell = (props: CommunityOptInShellProps) => {
  const [state] = useCommunityOptIn();

  return (
    <Box>
      {state.currentState === CommunityOptInStateType.ACCEPTED ? (
        props.children
      ) : (
        <CommunityOptIn />
      )}
    </Box>
  );
};

const CommunityOptIn = () => {
  return (
    <Stack alignItems={'flex-start'} sx={{ mb: 8 }}>
      <Typography
        variant={'h6'}
        sx={{
          p: 10,
          fontWeight: 500,
          textAlign: 'start',
        }}
      >
        <FormattedMessage id='community.title' />
      </Typography>
      <CommunityOptInSection />
    </Stack>
  );
};

const CommunityOptInSection = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [state, dispatch] = useCommunityOptIn();
  const [, dispatchUserInfo] = useUserInfoContext();
  const acceptSla = useCallback(async () => {
    await acceptCommunityOptIn(dispatch, dispatchUserInfo);
  }, [dispatch, dispatchUserInfo]);

  if (state.currentState === CommunityOptInStateType.ACCEPTING) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Grid item container alignItems={'center'} direction='column'>
        <ImageWithLoader
          src='/public/assets/images/illustration_community1.svg'
          style={{
            width: '100%',
            objectFit: 'contain',
          }}
          loaderSxProps={{ minHeight: 200 }}
        />
        <Typography
          variant={'h5'}
          sx={{ py: 10, fontWeight: 600, textAlign: 'center' }}
        >
          <FormattedMessage id='community.setup-1.title' />
        </Typography>
        <Box
          sx={{
            px: { xs: 18.5, lg: 37 },
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            pb: 15,
          }}
        >
          <Typography sx={{ lineHeight: 1.625 }}>
            <FormattedMessage id='community.setup-1.description' />
          </Typography>
          <Typography sx={{ lineHeight: 1.625 }}>
            <FormattedMessage id='community.setup-2.description' />
          </Typography>
          <Typography sx={{ lineHeight: 1.625 }}>
            <FormattedMessage id='community.setup-3.description' />
          </Typography>
          <Typography sx={{ lineHeight: 1.625 }}>
            <FormattedMessage
              id='community.setup-4.description'
              values={{
                tusLink: (text) => (
                  <Link
                    rel='noopener'
                    variant='body1'
                    href={TERMS_CONDITIONS_URL}
                    target='_blank'
                  >
                    {text}
                  </Link>
                ),
              }}
            />
          </Typography>
        </Box>
        <Grid
          item
          container
          direction={'row'}
          justifyContent={'space-around'}
          spacing={4}
          sx={{
            px: { xs: 7.5, lg: 15 },
          }}
        >
          <Grid item xs={6} md={4}>
            <Button
              fullWidth
              variant='outlined'
              size='large'
              data-testid='submitOtpButton'
              onClick={() => navigate(-1)}
              disableElevation
              sx={{
                mt: 3,
                mb: 4,
                borderRadius: 30,
                py: 3,
              }}
            >
              <FormattedMessage id='actions.later' />
            </Button>
          </Grid>
          <Grid item xs={6} md={4}>
            <Button
              fullWidth
              variant='contained'
              size='large'
              data-testid='submitOtpButton'
              sx={{
                mt: 3,
                mb: 4,
                borderRadius: 30,
                py: 3.5,
              }}
              onClick={acceptSla}
              disableElevation
            >
              <FormattedMessage
                id={
                  state.hasWithdrawnBefore
                    ? 'actions.rejoin'
                    : 'actions.get-started'
                }
              />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <MessageBar
        open={state.currentState === CommunityOptInStateType.ACCEPTING_FAILED}
        severity='error'
        message={intl.formatMessage({ id: 'common.error.message' })}
      />
    </>
  );
};
